import React from 'react';
import {
  FiUsers,
  FiSend,
  FiPhone,
  FiMessageSquare,
  FiGift,
  FiArrowUp,
  FiArrowDown,
  FiMinus,
} from 'react-icons/fi';

/**
 * Cohort KPI strip — the mentor analog of `StreakBar`. Five tiles laid out
 * with a Tailwind grid that collapses to two columns on small screens. The
 * cohort version trades the personal streak metric for "active mentees" and
 * adds an open-tickets and lifetime-gifted footer card, because those are
 * the numbers a mentor most often acts on.
 */
const formatDelta = (delta) => {
  if (delta === null || delta === undefined) return null;
  const pct = Math.round(delta * 100);
  if (pct === 0) return { label: '0%', tone: 'flat' };
  return {
    label: `${pct > 0 ? '+' : ''}${pct}%`,
    tone: pct > 0 ? 'up' : 'down',
  };
};

const TrendBadge = ({ delta, invertColors = false }) => {
  const parsed = formatDelta(delta);
  if (!parsed) return null;
  const positive = invertColors ? parsed.tone === 'down' : parsed.tone === 'up';
  const Icon = parsed.tone === 'up' ? FiArrowUp : parsed.tone === 'down' ? FiArrowDown : FiMinus;
  const color =
    parsed.tone === 'flat'
      ? 'text-gray-400'
      : positive
      ? 'text-green-600'
      : 'text-orange-600';
  return (
    <span className={`flex items-center gap-0.5 text-[11px] font-semibold ${color}`}>
      <Icon className="h-3 w-3" />
      {parsed.label}
    </span>
  );
};

const Tile = ({ icon, iconBg, label, value, sublabel, badge }) => (
  <div className="px-4 py-3 flex items-center gap-3 min-w-0">
    <div className={`flex-shrink-0 p-2 rounded-lg ${iconBg}`}>{icon}</div>
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-lg font-bold text-gray-900 truncate">{value}</span>
        {badge}
      </div>
      <div className="text-[11px] text-gray-500 truncate">{sublabel || label}</div>
    </div>
  </div>
);

const CohortKpiBar = ({ data }) => {
  const safe = data || {};
  const activeMentees = safe.activeMentees ?? 0;
  const totalMentees = safe.totalMentees ?? 0;
  const appsThisWeek = safe.appsThisWeek ?? 0;
  const interviewsThisWeek = safe.interviewsThisWeek ?? 0;
  const openTickets = safe.openTickets ?? 0;
  const oldestTicketDays = safe.oldestTicketDays ?? 0;
  const lifetimeGifted = safe.lifetimeGifted ?? 0;

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
      <div className="grid grid-cols-2 lg:grid-cols-5 divide-x divide-gray-100">
        <Tile
          icon={<FiUsers className="h-4 w-4 text-primary-600" />}
          iconBg="bg-primary-50"
          label="Active mentees"
          value={`${activeMentees} / ${totalMentees}`}
          sublabel="Active in the last 7 days"
        />
        <Tile
          icon={<FiSend className="h-4 w-4 text-blue-600" />}
          iconBg="bg-blue-50"
          label="Apps this week"
          value={appsThisWeek}
          sublabel="Cohort applications this week"
          badge={<TrendBadge delta={safe.appsDeltaPct} />}
        />
        <Tile
          icon={<FiPhone className="h-4 w-4 text-amber-600" />}
          iconBg="bg-amber-50"
          label="Interviews this week"
          value={interviewsThisWeek}
          sublabel="New interview rounds this week"
          badge={<TrendBadge delta={safe.interviewsDeltaPct} />}
        />
        <Tile
          icon={<FiMessageSquare className="h-4 w-4 text-rose-600" />}
          iconBg="bg-rose-50"
          label="Open tickets"
          value={openTickets}
          sublabel={
            openTickets > 0
              ? `Oldest: ${oldestTicketDays}d`
              : 'No tickets waiting'
          }
        />
        <Tile
          icon={<FiGift className="h-4 w-4 text-emerald-600" />}
          iconBg="bg-emerald-50"
          label="Credits gifted"
          value={lifetimeGifted.toLocaleString()}
          sublabel="Lifetime to your mentees"
        />
      </div>
    </div>
  );
};

export default CohortKpiBar;
